import { differenceInDays, format, isSameDay, parse, subDays } from 'date-fns';
import { useMemo } from 'react';
import { chartDateRangeOptions } from '@/modules/common/constants/chart-date-range-oprions.ts';
import { BalanceChartCanvas, SelectButtons, Spinner } from 'ui';
import { useIsMobile } from 'ui/hooks';
import { ApexChartSeries } from 'ui/types';
import { TotalBalanceByDateResponse } from 'api';

export interface InvestorDashboardBalanceChartProps {
  handleChangeDateRange: (date_range: string) => void;
  date_range: string;
  balances?: TotalBalanceByDateResponse[];
  current_balance?: number;
  isLoading: boolean;
}

export const InvestorDashboardBalanceChart = ({
  handleChangeDateRange,
  date_range,
  balances,
  current_balance,
  isLoading,
}: InvestorDashboardBalanceChartProps) => {
  const { isMobile } = useIsMobile();

  const balancesWithActualData = useMemo<TotalBalanceByDateResponse[]>(() => {
    if (!balances || typeof current_balance !== 'number') return [];

    const lastData =
      balances?.at(-1)?.calculate_date &&
      !isSameDay(
        new Date(balances?.at(-1)?.calculate_date || subDays(new Date(), 1)),
        new Date(),
      ) &&
      differenceInDays(
        new Date(balances?.at(-1)?.calculate_date || subDays(new Date(), 1)),
        new Date(),
      ) < 0
        ? {
            amount: current_balance ?? 0,
            calculate_date: format(new Date(), 'dd-MM-yyyy'),
          }
        : undefined;

    let data: TotalBalanceByDateResponse[] =
      balances?.length > 0
        ? [
            // {
            //   amount: 0,
            //   calculate_date: format(
            //     subDays(
            //       balances?.[0]?.calculate_date
            //         ? new Date(balances?.[0]?.calculate_date)
            //         : new Date(),
            //       1,
            //     ),
            //     'dd-MM-yyyy',
            //   ),
            // },
            ...balances.map(item => ({
              ...item,
              calculate_date: format(
                parse(item.calculate_date || '', 'yyyy-MM-dd', new Date()),
                'dd-MM-yyyy',
              ),
            })),
          ]
        : [
            {
              amount: 0,
              calculate_date: format(subDays(new Date(), 1), 'dd-MM-yyyy'),
            },
          ];

    data = lastData ? [...data, lastData] : data;

    return data;
  }, [current_balance, balances]);

  const chartSeries = useMemo<ApexChartSeries[]>(() => {
    return [
      {
        name: 'Amount',
        data:
          balancesWithActualData?.map(({ amount, calculate_date }) => ({
            y: amount,
            x: calculate_date,
          })) || [],
      },
    ];
  }, [balancesWithActualData]);

  return (
    <div className="w-full h-[350] flex flex-col items-center border border-gray-200 rounded-xl p-3 sm:p-6">
      <div className="w-full flex flex-wrap items-center justify-between">
        <p className="text-[18px] leading-[24px] font-medium">Total balance</p>
        <SelectButtons
          options={chartDateRangeOptions}
          selected={
            chartDateRangeOptions.find(({ value }) => value === date_range) ||
            chartDateRangeOptions[4]
          }
          className="sm:w-[375px] w-[300px]"
          onChange={option => handleChangeDateRange(option)}
        />
      </div>
      <div className="w-full">
        {isLoading ? (
          <div className="w-full h-[300px] flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : (
          <BalanceChartCanvas
            data={chartSeries}
            height={isMobile ? '430px' : '300px'}
            countsLabel={isMobile ? 6 : 11}
            markerStrokeColor="#FFFFFF"
          />
        )}
      </div>
    </div>
  );
};
